/**
 * DeviceCheck Component
 * 
 * Pre-call device check for camera, microphone, and speakers.
 * 
 * Premium healthcare design with refined device status indicators.
 */

import React, { useEffect, useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { 
  Mic, 
  Video, 
  Volume2,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  User,
  ChevronRight
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface DeviceCheckProps {
  onComplete: () => void
  appointmentInfo?: {
    providerName?: string
    clinicName?: string
    startTime?: string
  }
}

interface DeviceStatus {
  camera: 'checking' | 'granted' | 'denied' | 'error'
  microphone: 'checking' | 'granted' | 'denied' | 'error'
  speaker: 'checking' | 'available' | 'error'
}

const DeviceCheck: React.FC<DeviceCheckProps> = ({ onComplete, appointmentInfo }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [status, setStatus] = useState<DeviceStatus>({
    camera: 'checking',
    microphone: 'checking',
    speaker: 'checking'
  })
  const [audioLevel, setAudioLevel] = useState(0)
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])
  const [selectedCamera, setSelectedCamera] = useState<string>('')
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const checkDevices = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        })

        setStream(mediaStream)

        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoInputs = devices.filter(d => d.kind === 'videoinput')
        const audioInputs = devices.filter(d => d.kind === 'audioinput')

        setCameras(videoInputs)
        setMicrophones(audioInputs)

        if (videoInputs.length > 0) {
          setSelectedCamera(videoInputs[0].deviceId)
        }
        if (audioInputs.length > 0) {
          setSelectedMicrophone(audioInputs[0].deviceId)
        }

        setStatus({
          camera: videoInputs.length > 0 ? 'granted' : 'error',
          microphone: audioInputs.length > 0 ? 'granted' : 'error',
          speaker: 'available'
        })

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream
        }

        const audioContext = new AudioContext()
        const analyser = audioContext.createAnalyser()
        const microphone = audioContext.createMediaStreamSource(mediaStream)
        microphone.connect(analyser)
        analyser.fftSize = 256
        const dataArray = new Uint8Array(analyser.frequencyBinCount)

        const checkAudioLevel = () => {
          analyser.getByteFrequencyData(dataArray)
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length
          setAudioLevel(Math.min(100, average * 2))
          requestAnimationFrame(checkAudioLevel)
        }
        checkAudioLevel()

      } catch (err) {
        console.error('Device check error:', err)
        
        if (err instanceof Error) {
          if (err.name === 'NotAllowedError') {
            setStatus({
              camera: 'denied',
              microphone: 'denied',
              speaker: 'available'
            })
            setError('Camera and microphone access was denied. Please allow access in your browser settings.')
          } else if (err.name === 'NotFoundError') {
            setStatus({
              camera: 'error',
              microphone: 'error',
              speaker: 'available'
            })
            setError('No camera or microphone found. Please connect a device and try again.')
          } else {
            setError(err.message)
          }
        }
      }
    }

    checkDevices()

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  useEffect(() => {
    if (!selectedCamera || !stream) return

    const updateCamera = async () => {
      const videoTrack = stream.getVideoTracks()[0]
      if (videoTrack) {
        videoTrack.stop()
      }

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: selectedCamera } },
        audio: { deviceId: selectedMicrophone ? { exact: selectedMicrophone } : undefined }
      })

      setStream(newStream)
      if (videoRef.current) {
        videoRef.current.srcObject = newStream
      }
    }

    updateCamera()
  }, [selectedCamera])

  const isReady = status.camera === 'granted' && status.microphone === 'granted'

  const handleContinue = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
    }
    onComplete()
  }

  const getStatusIcon = (deviceStatus: string) => {
    switch (deviceStatus) {
      case 'granted':
      case 'available':
        return <CheckCircle2 className="h-4 w-4 text-emerald-400" />
      case 'denied':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-400" />
      default:
        return <Loader2 className="h-4 w-4 animate-spin text-brand-teal" />
    }
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-5">
      {/* Appointment Info */}
      {appointmentInfo && (
        <div className="p-4 rounded-xl bg-[#1a2530] border border-brand-teal/20">
          <div className="flex items-center gap-2 mb-2 text-white font-medium text-sm">
            <User className="h-4 w-4 text-brand-teal" />
            {appointmentInfo.providerName}
          </div>
          {appointmentInfo.clinicName && (
            <div className="flex items-center gap-2 mb-1 text-slate-400 text-sm">
              <span className="w-4 h-4 flex items-center justify-center">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
              </span>
              {appointmentInfo.clinicName}
            </div>
          )}
          {appointmentInfo.startTime && (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <span className="w-4 h-4 flex items-center justify-center">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
              </span>
              {formatTime(appointmentInfo.startTime)}
            </div>
          )}
        </div>
      )}

      {/* Video Preview */}
      <div className="relative aspect-video rounded-xl overflow-hidden bg-[rgb(10_15_20)] border border-slate-700/40">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={cn(
            "w-full h-full object-cover transition-opacity duration-300",
            status.camera !== 'granted' && 'opacity-0'
          )}
          style={{ transform: 'scaleX(-1)' }}
        />
        {status.camera !== 'granted' && (
          <div className="absolute inset-0 flex items-center justify-center bg-[rgb(10_15_20)]">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#1a2530] border border-slate-700/50 flex items-center justify-center">
                <Video className="h-8 w-8 text-slate-500" />
              </div>
              <p className="text-slate-400 text-sm">Camera not available</p>
            </div>
          </div>
        )}
      </div>

      {/* Device Status Cards */}
      <div className="space-y-2">
        {/* Camera */}
        <div className={cn(
          "flex items-center justify-between p-3.5 rounded-xl border transition-all",
          status.camera === 'granted' 
            ? "bg-[#1a2530] border-slate-700/40" 
            : "bg-red-500/5 border-red-500/20"
        )}>
          <div className="flex items-center gap-3">
            {getStatusIcon(status.camera)}
            <div className="flex items-center gap-2.5">
              <Video className="h-4 w-4 text-slate-400" />
              <span className="font-medium text-white text-sm">Camera</span>
            </div>
          </div>
          {cameras.length > 0 && (
            <Select value={selectedCamera} onValueChange={setSelectedCamera}>
              <SelectTrigger className="w-44 h-8 text-xs bg-[#141c24] border-slate-600/50 text-slate-200 hover:border-slate-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141c24] border-slate-700">
                {cameras.map(camera => (
                  <SelectItem 
                    key={camera.deviceId} 
                    value={camera.deviceId} 
                    className="text-white text-xs hover:bg-[#1a2530] focus:bg-[#1a2530]"
                  >
                    {camera.label || `Camera ${cameras.indexOf(camera) + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Microphone */}
        <div className={cn(
          "flex items-center justify-between p-3.5 rounded-xl border transition-all",
          status.microphone === 'granted' 
            ? "bg-[#1a2530] border-slate-700/40" 
            : "bg-red-500/5 border-red-500/20"
        )}>
          <div className="flex items-center gap-3">
            {getStatusIcon(status.microphone)}
            <div className="flex items-center gap-2.5">
              <Mic className="h-4 w-4 text-slate-400" />
              <span className="font-medium text-white text-sm">Microphone</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Audio level indicator */}
            {status.microphone === 'granted' && (
              <div className="w-20 h-2 bg-[#141c24] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-brand-teal transition-all duration-75"
                  style={{ width: `${audioLevel}%` }}
                />
              </div>
            )}
            {microphones.length > 0 && (
              <Select value={selectedMicrophone} onValueChange={setSelectedMicrophone}>
                <SelectTrigger className="w-32 h-8 text-xs bg-[#141c24] border-slate-600/50 text-slate-200 hover:border-slate-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141c24] border-slate-700">
                  {microphones.map(mic => (
                    <SelectItem 
                      key={mic.deviceId} 
                      value={mic.deviceId} 
                      className="text-white text-xs hover:bg-[#1a2530] focus:bg-[#1a2530]"
                    >
                      {mic.label || `Mic ${microphones.indexOf(mic) + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Speaker */}
        <div className="flex items-center justify-between p-3.5 rounded-xl bg-[#1a2530] border border-slate-700/40">
          <div className="flex items-center gap-3">
            {getStatusIcon(status.speaker)}
            <div className="flex items-center gap-2.5">
              <Volume2 className="h-4 w-4 text-slate-400" />
              <span className="font-medium text-white text-sm">Speaker</span>
            </div>
          </div>
          <Badge 
            variant="outline" 
            className="text-[10px] border-slate-600/50 text-slate-400 bg-[#141c24] font-normal"
          >
            System Default
          </Badge>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-500/10 rounded-xl border border-red-500/20">
          <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-200/90">{error}</p>
        </div>
      )}

      {/* Continue Button */}
      <Button 
        onClick={handleContinue} 
        disabled={!isReady}
        className={cn(
          "w-full h-11 font-medium transition-all duration-200",
          isReady 
            ? "bg-brand-teal hover:bg-brand-teal/90 text-white" 
            : "bg-slate-700 text-slate-500 cursor-not-allowed"
        )}
      >
        {isReady ? (
          <>
            Continue to Waiting Room
            <ChevronRight className="h-4 w-4 ml-2" />
          </>
        ) : (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Checking devices...
          </>
        )}
      </Button>

      {!isReady && (
        <p className="text-xs text-center text-slate-500">
          Please allow camera and microphone access to continue
        </p>
      )}
    </div>
  )
}

export default DeviceCheck
